import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const PaginationButtons = ({
    currentPage = 1,
    totalPages = 0,
    onPageChange,
}: {
    currentPage?: number;
    totalPages?: number;
    onPageChange: (page: number) => void;
}) => {
    const [jumpToPage, setJumpToPage] = useState("");

    // guard: no pages or invalid numbers
    if (!totalPages || totalPages < 1 || isNaN(totalPages)) {
        return null;
    }

    const safeCurrent = Math.min(Math.max(currentPage, 1), totalPages);

    const getPageNumbers = () => {
        const pageNumbers: (number | string)[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
            return pageNumbers;
        }

        pageNumbers.push(1);

        let rangeStart = Math.max(2, safeCurrent - 1);
        let rangeEnd = Math.min(totalPages - 1, safeCurrent + 1);

        if (safeCurrent <= 3) rangeEnd = Math.min(4, totalPages - 1);
        else if (safeCurrent >= totalPages - 2)
            rangeStart = Math.max(2, totalPages - 3);

        if (rangeStart > 2) pageNumbers.push("...");
        for (let i = rangeStart; i <= rangeEnd; i++) pageNumbers.push(i);
        if (rangeEnd < totalPages - 1) pageNumbers.push("...");
        if (totalPages > 1) pageNumbers.push(totalPages);

        return pageNumbers;
    };

    const handleJumpToPage = () => {
        const pageNumber = parseInt(jumpToPage);
        if (!isNaN(pageNumber)) {
            const safePage = Math.min(Math.max(pageNumber, 1), totalPages);
            if (safePage !== safeCurrent) onPageChange(safePage);
        }
        setJumpToPage("");
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") handleJumpToPage();
    };

    return (
        <div className="flex flex-wrap items-center gap-2">
            {/* Page number buttons */}
            <div className="flex items-center gap-1">
                {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                        <span
                            key={`ellipsis-${index}`}
                            className="flex h-9 w-9 items-center justify-center text-sm font-medium text-muted-foreground"
                        >
                            …
                        </span>
                    ) : (
                        <Button
                            key={pageNum}
                            variant={
                                Number(pageNum) === safeCurrent
                                    ? "default"
                                    : "outline"
                            }
                            size="sm"
                            onClick={() =>
                                Number(pageNum) !== safeCurrent &&
                                onPageChange(Number(pageNum))
                            }
                            className="h-9 w-9 p-0 font-medium transition-all hover:bg-accent"
                            aria-label={`Go to page ${pageNum}`}
                            aria-current={
                                Number(pageNum) === safeCurrent
                                    ? "page"
                                    : undefined
                            }
                        >
                            {pageNum}
                        </Button>
                    ),
                )}
            </div>

            {/* Jump to page input */}
            <div className="ml-1 flex items-center gap-2 border-l pl-3">
                <Label className="whitespace-nowrap text-xs font-medium text-muted-foreground">
                    Jump to:
                </Label>
                <Input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={jumpToPage}
                    onChange={(e) => setJumpToPage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="h-9 w-16 text-sm transition-all focus:ring-2"
                    placeholder={String(safeCurrent)}
                    aria-label="Jump to page number"
                />
                <Button
                    size="sm"
                    variant="outline"
                    onClick={handleJumpToPage}
                    disabled={
                        !jumpToPage ||
                        isNaN(parseInt(jumpToPage)) ||
                        parseInt(jumpToPage) === safeCurrent ||
                        parseInt(jumpToPage) < 1 ||
                        parseInt(jumpToPage) > totalPages
                    }
                    className="h-9 px-3 font-medium transition-all hover:bg-accent disabled:opacity-50"
                >
                    Go
                </Button>
            </div>
        </div>
    );
};

export default PaginationButtons;
